// Demo: role selection (anonymous sign-in + demo-session, PRD §3.5). Data: e-mail link sign-in (PRD §3.3).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ErrorBox } from '../components/ui.tsx';
import { env } from '../env.ts';
import { invoke, operatorClient, publicClient } from '../lib/supabase.ts';

const ROLES = [
  'data_provider',
  'quality_manager',
  'regulatory_owner',
  'auditor',
  'authority',
  'platform_admin',
];

export function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sample, setSample] = useState<string | null>(null);

  // the newest published passport, read like any visitor reads it (public view, no sign-in)
  useEffect(() => {
    void publicClient()
      .from('public_passport')
      .select('upi')
      .order('published_at', { ascending: false })
      .limit(1)
      .then(({ data }) => setSample((data?.[0] as { upi: string } | undefined)?.upi ?? null));
  }, []);

  // data site: an invitation or sign-in link lands here with the session in the address; the operator client reads
  // it, and a signed-in user goes on to the batches (the demo keeps the role choice, so a role can be changed)
  useEffect(() => {
    if (env.environment === 'demo') return;
    const sb = operatorClient();
    const go = (signedIn: boolean) => signedIn && void navigate('/batches', { replace: true });
    void sb.auth.getSession().then(({ data }) => go(!!data.session));
    const { data } = sb.auth.onAuthStateChange((_event, session) => go(!!session));
    return () => data.subscription.unsubscribe();
  }, [navigate]);

  async function chooseRole(role: string) {
    setBusy(true);
    setError(null);
    try {
      const sb = operatorClient();
      const { data } = await sb.auth.getSession();
      if (!data.session) {
        const { error: e } = await sb.auth.signInAnonymously();
        if (e) throw e;
      }
      await invoke('demo-session', { role });
      await sb.auth.refreshSession();
      void navigate('/batches');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function sendLink(ev: React.FormEvent) {
    ev.preventDefault();
    // shouldCreateUser false: public sign-up is disabled; the answer is the same whether or not the address exists
    await operatorClient().auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: window.location.origin + '/batches' },
    });
    setSent(true);
  }

  return (
    <main className="landing">
      <section className="hero">
        <h1>{t('app.title')}</h1>
        <p>{t('landing.intro')}</p>
        {sample && (
          <p>
            <Link to={`/p/${encodeURIComponent(sample)}`}>{t('landing.sample_passport')}</Link>
          </p>
        )}
      </section>
      {env.environment === 'demo' && (
        <section>
          <h2>{t('steps.title')}</h2>
          <ol className="steps">
            {[1, 2, 3, 4, 5].map((n) => (
              <li key={n}>{t(`steps.${n}`)}</li>
            ))}
          </ol>
        </section>
      )}
      {env.environment === 'demo' ? (
        <section>
          <h2>{t('landing.choose_role')}</h2>
          <p className="muted">{t('landing.role_note')}</p>
          <div className="roles">
            {ROLES.map((r) => (
              <button
                key={r}
                className="role-card"
                disabled={busy}
                aria-label={t(`role.${r}`)}
                aria-describedby={`role-desc-${r}`}
                onClick={() => void chooseRole(r)}
              >
                <span className="role-name">{t(`role.${r}`)}</span>
                <span className="role-desc" id={`role-desc-${r}`}>
                  {t(`roledesc.${r}`)}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className="card">
          <h2>{t('landing.email')}</h2>
          {sent ? (
            <p>{t('landing.link_sent')}</p>
          ) : (
            <form className="form" onSubmit={(e) => void sendLink(e)}>
              <label>
                {t('landing.email_label')}
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <button type="submit">{t('landing.send_link')}</button>
            </form>
          )}
        </section>
      )}
      <ErrorBox error={error} />
    </main>
  );
}
